import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Half-finished tool answers, kept in the reader's own browser.
 *
 * Fatih, 2026-09-04: *"transformation readiness 5. soruda bıraktım sayfayı
 * refreshledim ya da başka bir sayfaya girip çıktım ilerlemem kayboldu. Bu
 * ilerlemem kaybolmasa kayıtlı kalsa hatta ... 2 gün sonra tekrar açtığımda
 * continue from where you've left pop up ı çıksa"*.
 *
 * ---------------------------------------------------------------------------
 * WHY `localStorage` AND NOT OUR DATABASE. This is the whole design decision,
 * and it was Kagan's (2026-09-08).
 *
 * The obvious implementation is a row keyed on an anonymous id, which would
 * also survive a change of device and would let us report "how many people
 * abandon at question 5" — genuinely useful to marketing. It was declined, and
 * the reason is the shape of the request rather than the effort: at the moment
 * these answers exist, **the reader has not given us their email and has not
 * agreed to anything.** The gate comes later. Storing them server-side turns
 * eight answers about somebody's employer into personal data we process, which
 * needs a lawful basis, a retention period, a deletion path and a line in the
 * privacy notice — for a feature whose entire purpose is convenience.
 *
 * Kept in the browser, none of that applies: the data never reaches us, so
 * there is nothing to disclose, nothing to retain and nothing to delete on
 * request. **This is a boundary, not an implementation detail — do not "improve"
 * it by adding a fetch.** If cross-device resume or abandonment analytics are
 * ever wanted, that is a new decision with a privacy notice attached, not a
 * refactor of this file.
 *
 * The costs, stated so nobody reports them as bugs: progress does not follow a
 * reader to another device or another browser, a private window forgets it on
 * close, and we cannot see it. All three are the point.
 *
 * ---------------------------------------------------------------------------
 * THREE THINGS THAT WOULD EACH BREAK THIS QUIETLY.
 *
 * 1. RESTORE HAPPENS IN AN EFFECT, NEVER IN `useState`'s INITIALISER. Every
 *    tool page is prerendered to static HTML, so the server's first render
 *    cannot know what is in a browser it has never seen. Seeding state from
 *    storage would make the client's first render disagree with the served
 *    markup — a hydration mismatch, which React resolves by throwing away the
 *    DOM it was given. So the first render matches the server, and the restore
 *    lands one tick later.
 *
 * 2. NOTHING IS WRITTEN UNTIL THE READ HAS HAPPENED. Otherwise the first
 *    persist — running with the default empty answers, before the effect that
 *    restores — overwrites the very progress it is meant to protect. `ready`
 *    guards it, and it is the reason this is a hook rather than two `useEffect`s
 *    copied into four files.
 *
 * 3. EVERY ACCESS IS WRAPPED. `localStorage` is not merely empty in a private
 *    window or with site data blocked — the accessor itself THROWS, and an
 *    exception thrown from a render or an effect on a marketing page takes the
 *    whole tool down. A reader who has disabled storage should lose the
 *    convenience, not the tool.
 *
 * ---------------------------------------------------------------------------
 * WHY THE SHAPE IS FINGERPRINTED. Answers are stored as INDEXES into the
 * question set, so restoring them against a different set silently scores the
 * reader on answers they never gave. The fingerprint is taken from each tool's
 * SCORES array — which lives outside the locale dictionary precisely so no
 * translation can alter it — and that draws the line in exactly the right
 * place: editing the wording of question 5 keeps the saved answers valid, while
 * adding, removing or reordering a question discards them. A version string is
 * folded in for changes the numbers cannot see.
 */

const VERSION = 'v1'

/** Older than this and we start clean rather than resurrect a stranger's month-old session. */
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000

/** Anything newer than this is a page refresh, not a return visit. */
export const RESUMED_QUIETLY_MS = 30 * 60 * 1000

interface Stored<T> {
  /** Fingerprint of the question set the answers were given against. */
  s: string
  /** When it was last written, epoch ms. */
  at: number
  state: T
}

const keyFor = (tool: string) => `dp.tool.${tool}.${VERSION}`

/**
 * A small, stable, order-sensitive hash. Not a checksum for anything that
 * matters — it only has to change when the question set does.
 */
export function fingerprint(scores: unknown): string {
  const text = JSON.stringify(scores)
  let h = 5381
  for (let i = 0; i < text.length; i++) h = ((h << 5) + h + text.charCodeAt(i)) | 0
  return `${VERSION}.${text.length}.${(h >>> 0).toString(36)}`
}

function read<T>(tool: string, shape: string): Stored<T> | null {
  try {
    const raw = window.localStorage.getItem(keyFor(tool))
    if (!raw) return null
    const parsed = JSON.parse(raw) as Stored<T>
    if (!parsed || typeof parsed !== 'object') return null
    // A changed question set, or one left too long, is discarded rather than
    // migrated: there is no honest way to map old indexes onto new questions.
    if (parsed.s !== shape) return null
    if (typeof parsed.at !== 'number' || Date.now() - parsed.at > MAX_AGE_MS) return null
    return parsed
  } catch {
    return null
  }
}

export function forgetProgress(tool: string): void {
  try {
    window.localStorage.removeItem(keyFor(tool))
  } catch {
    /* nothing to do — see note 3 */
  }
}

/**
 * Persist `state` for one tool, restore it once on mount, and say whether the
 * reader is coming back rather than refreshing.
 *
 * `active` is what keeps the stored value honest: pass false once the run is
 * over (the report is on screen, or the lead has been submitted) so a finished
 * session is not offered back as unfinished work. Call `forget` when the reader
 * chooses to start over.
 *
 * `restore` receives whatever was stored — treat it as UNTRUSTED. It is JSON
 * from the reader's own browser, editable by them and possibly written by an
 * older build; validate before applying, which is why the tools pass a function
 * rather than handing over their setters.
 *
 * Its second argument is how long ago the progress was written, because the two
 * cases Fatih described want different behaviour and only the age separates
 * them. Under `RESUMED_QUIETLY_MS` this is a page refresh — put the reader back
 * where they were and say nothing, since announcing a restore they did not
 * notice losing is just noise. Older than that it is a return visit, and being
 * dropped into question 5 of a quiz you half remember is disorienting, so the
 * tool offers it instead.
 */
export function useToolProgress<T>(opts: {
  tool: string
  shape: string
  state: T
  active: boolean
  restore: (saved: T, ageMs: number) => void
}): { resumedAt: number | null; forget: () => void } {
  const { tool, shape, state, active, restore } = opts

  const [resumedAt, setResumedAt] = useState<number | null>(null)
  const ready = useRef(false)
  // `restore` is a fresh closure every render; the effect below must run once,
  // so it reads the latest one through a ref instead of depending on it.
  const restoreRef = useRef(restore)
  restoreRef.current = restore

  useEffect(() => {
    const found = read<T>(tool, shape)
    if (found) {
      try {
        restoreRef.current(found.state, Date.now() - found.at)
        setResumedAt(found.at)
      } catch {
        // A stored shape the current build cannot make sense of. Drop it rather
        // than leave a value that fails the same way on every visit.
        forgetProgress(tool)
      }
    }
    // Only now may writing begin — see note 2.
    ready.current = true
  }, [tool, shape])

  useEffect(() => {
    if (!ready.current) return
    if (!active) return
    try {
      const payload: Stored<T> = { s: shape, at: Date.now(), state }
      window.localStorage.setItem(keyFor(tool), JSON.stringify(payload))
    } catch {
      /* see note 3 */
    }
  }, [tool, shape, state, active])

  const forget = useCallback(() => {
    forgetProgress(tool)
    setResumedAt(null)
  }, [tool])

  return { resumedAt, forget }
}
